'use client';

import React from 'react';
import { AlertTriangle, Cpu, Clock, TrendingDown, TrendingUp, Minus, ShieldAlert } from 'lucide-react';
import type { KpiMetric } from '@/hooks/useDashboardRealtime';
import Icon from '@/components/ui/AppIcon';


const iconMap: Record<string, React.ElementType> = {
  AlertTriangle,
  ShieldAlert,
  Clock,
  Cpu,
};

const defaultKpis = [
  { id: 'kpi-p1', metric_key: 'kpi-p1', label: 'Active P1 Incidents', value: '—', sub_label: '—', trend: 'neutral', trend_val: '—', variant: 'danger' },
  { id: 'kpi-p2', metric_key: 'kpi-p2', label: 'Active P2 Incidents', value: '—', sub_label: '—', trend: 'neutral', trend_val: '—', variant: 'warning' },
  { id: 'kpi-mttd', metric_key: 'kpi-mttd', label: 'Avg MTTD', value: '—', sub_label: '—', trend: 'neutral', trend_val: '—', variant: 'success' },
  { id: 'kpi-mttr', metric_key: 'kpi-mttr', label: 'Avg MTTR', value: '—', sub_label: '—', trend: 'neutral', trend_val: '—', variant: 'warning' },
  { id: 'kpi-automation', metric_key: 'kpi-automation', label: 'Automation Success', value: '—', sub_label: '—', trend: 'neutral', trend_val: '—', variant: 'success' },
  { id: 'kpi-nodes', metric_key: 'kpi-nodes', label: 'Nodes at Risk', value: '—', sub_label: '—', trend: 'neutral', trend_val: '—', variant: 'danger' },
];

const iconForKey = (key: string): React.ElementType => {
  if (key.includes('p1') || key.includes('nodes')) return AlertTriangle;
  if (key.includes('p2')) return ShieldAlert;
  if (key.includes('mttd') || key.includes('mttr')) return Clock;
  return Cpu;
};

const variantStyles: Record<string, { bg: string; icon: string; border: string }> = {
  danger: { bg: 'bg-danger/8', icon: 'text-danger', border: 'border-danger/20' },
  warning: { bg: 'bg-warning/8', icon: 'text-warning', border: 'border-warning/20' },
  success: { bg: 'bg-success/8', icon: 'text-success', border: 'border-success/20' },
  info: { bg: 'bg-info/8', icon: 'text-info', border: 'border-info/20' },
};

interface Props {
  kpis: KpiMetric[];
  loading: boolean;
}

export default function KpiGrid({ kpis, loading }: Props) {
  const displayKpis = loading || kpis.length === 0 ? defaultKpis : kpis;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-3 h-full">
      {displayKpis.map((kpi) => {
        const Icon = iconForKey(kpi.metric_key);
        const v = variantStyles[kpi.variant] ?? variantStyles.info;
        const TrendIcon = kpi.trend === 'up' ? TrendingUp : kpi.trend === 'down' ? TrendingDown : Minus;
        const trendColor =
          (kpi.variant === 'danger' && kpi.trend === 'up') ||
          (kpi.variant === 'warning' && kpi.trend === 'up')
            ? 'text-danger'
            : kpi.trend === 'down' && kpi.variant === 'success'
            ? 'text-success' :'text-muted';

        return (
          <div
            key={kpi.metric_key}
            className={`${v.bg} border ${v.border} rounded-xl p-4 flex flex-col justify-between hover:border-opacity-40 transition-all duration-150`}
          >
            <div className="flex items-start justify-between mb-3">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted leading-tight">
                {kpi.label}
              </p>
              <Icon size={15} className={v.icon} />
            </div>
            <div>
              <p className={`text-2xl font-bold font-mono-data text-foreground leading-none mb-1 ${loading ? 'animate-pulse' : ''}`}>
                {kpi.value}
              </p>
              <p className="text-[11px] text-muted">{kpi.sub_label}</p>
            </div>
            <div className={`flex items-center gap-1 mt-2 text-[10px] font-medium ${trendColor}`}>
              <TrendIcon size={10} />
              <span>{kpi.trend_val}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}